import { Injectable, NgZone } from '@angular/core';
import { fromEvent, merge, timer, Observable, Subject, Subscription } from 'rxjs';
import { startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class IdleService {
    private activityEvents$ = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'click'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'scroll')
    );

    // Public observable to track the idle state
    public onIdle = new Subject<boolean>();
    private subscription: Subscription | undefined;
    private isIdle = false;

    // Set your idle time here (e.g., 5 minutes = 300000ms)
    public IDLE_TIMEOUT_MS: number = 180000;

    constructor(private ngZone: NgZone) {
    }

    public setIdleTimeout(ms: number) {
        this.IDLE_TIMEOUT_MS = ms;
    }
    
    public startWatching(): void {
        if (this.subscription) return;

        const idle$ = this.activityEvents$.pipe(
            // Emit an initial value to start the timer right away
            startWith(false),

            // If the user was idle, emit the wake event.
            tap(() => this.handleActivity()),

            // Switch to a new timer and run outside Angular Zone for performance
            switchMap(() => this.ngZone.runOutsideAngular(() => timer(this.IDLE_TIMEOUT_MS))),

            // When the timer completes (timeout), emit the idle event
            tap(() => this.handleTimeout())
        );

        this.subscription = idle$.subscribe();
    }

    public stopWatching(): void {
        this.subscription?.unsubscribe();
    }

    private handleActivity(): void {
        // If the user was previously marked as idle, and now an activity happened, they've 'woken up'.
        if (this.isIdle) {
            this.isIdle = false;
            // 3. Emit the wake event *inside* the Angular Zone
            this.ngZone.run(() => this.onIdle.next(false));
        }
    }
    private handleTimeout(): void {
        this.isIdle = true;
        // 4. Emit the idle event *inside* the Angular Zone
        this.ngZone.run(() => this.onIdle.next(true));
    }
}